/*
 * @Copyright 2023-2026. Institute for Future Intelligence, Inc.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { i18n_en } from './en';

// Only English is bundled eagerly. The two Chinese locales are ~400 KB of
// source between them and a session uses exactly one language, so shipping all
// three in the entry chunk made every user parse two translation tables they
// would never read. They are registered on demand through ensureLocale below.
//
// English stays eager because it is both the default and the fallback: if a
// Chinese bundle is still in flight (or fails to load), i18next needs a
// complete resource set to render against rather than bare key names.
i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    debug: true,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    resources: {
      en: i18n_en,
      // es: i18n_es,
      // tr: i18n_tr,
      // ua: i18n_ua,
    },
  });

const LOCALE_LOADERS: Record<string, () => Promise<Record<string, any>>> = {
  zh_cn: () => import('./zh_cn').then((m) => m.i18n_zh_cn),
  zh_tw: () => import('./zh_tw').then((m) => m.i18n_zh_tw),
};

// Cache the in-flight promise rather than a done-flag so concurrent callers
// (the boot path and a fast language switch) share a single fetch.
const localePromises = new Map<string, Promise<void>>();

/**
 * Make `lng` renderable, fetching its bundle if it isn't loaded yet. Safe to
 * call with 'en' or an unknown code — both resolve immediately. Callers that
 * can wait (app boot) should await it; the language dropdown may fire and
 * forget, since the re-emit below refreshes the UI once the strings land.
 */
export const ensureLocale = (lng: string | null | undefined): Promise<void> => {
  if (!lng || lng === 'en') return Promise.resolve();
  const cached = localePromises.get(lng);
  if (cached) return cached;
  const loader = LOCALE_LOADERS[lng];
  if (!loader) return Promise.resolve();
  const p = loader()
    .then((bundle) => {
      // Each bundle is { translation: {...} }. Register every namespace it
      // declares rather than assuming the single one, so adding a namespace
      // later doesn't silently drop it here.
      for (const [ns, resources] of Object.entries(bundle)) {
        i18n.addResourceBundle(lng, ns, resources, true, true);
      }
      // Most call sites read strings as i18n.t(key, { lng }) rather than
      // through useTranslation's bound t, so react-i18next has no reason to
      // re-render on its own. Re-emitting languageChanged forces subscribed
      // components to re-read now that the bundle exists.
      i18n.emit('languageChanged', i18n.language);
    })
    .catch((err) => {
      // Leave the app on the English fallback rather than failing the boot.
      console.error(`AIMS: failed to load locale ${lng}`, err);
      localePromises.delete(lng);
    });
  localePromises.set(lng, p);
  return p;
};

/**
 * The language the store will restore on boot, read straight from the
 * persisted zustand blob. Needed before React mounts: at that point the store
 * has not rehydrated, so useStore.getState().language is still the 'en'
 * default. Awaiting this is what keeps a Chinese user from seeing a flash of
 * English on every page load.
 */
export const getPersistedLanguage = (): string => {
  try {
    const raw = localStorage.getItem('aims-storage');
    if (!raw) return 'en';
    return JSON.parse(raw)?.state?.language ?? 'en';
  } catch {
    return 'en';
  }
};

export default i18n;
